"use strict";

const _ = require('lodash');
const TS = require("@zerolight/zlthunder-time-utils");

const toggles = require('../components/toggles');
const Controller = require('../shared/controller');
const APIController = require('../shared/api-controller');
const TimeRange = require('../components/time-range');
const FiltersController = require('../components/filters-hash');
const StackedBars = require("../shared/charts").StackedBarsChart;
const {formatDate, DateFormat} = require('../shared/formatters');

class DBars extends StackedBars.plug('dates') {
	constructor(... options) {
		super(... options);
	}

	setInteractBar(selection) {
		let self = this;
		let dates = this.options.dates;
		selection
			.attr('class', function(d, i) {
				let c = ['interact', 'bar'];
				if (!dates.has(d.key)) {
					c.push('shade');
				}
				return c.join(' ');
			})
			.on('click', function (d, i) {
				self.emit('cm:click', d, i);
			});
	}

	drawInteract(selection) {
		let s = super.drawInteract(selection);
		let self = this;
		let dates = this.options.dates;
		s.on('click', function (d, i) {
			dates.toggle(d.key);
			self.emit('cm:click', d, i);
		});
	}
}

class HBars extends StackedBars {
	constructor(... options) {
		super(... options);
	}
}

class DateListToggles extends toggles.SimpleToggles {
	constructor(values, options) {
		options = Object.assign({hash: _.toSafeInteger}, options);
		super(values, options);
	}

	toggle(value) {
		let t = this.getToggle(value);
		if (t) {
			this.items.delete(t.key);
		} else {
			this.add(value);
		}
		this.emit('cm:update', value);
	}

	has(date) {
		return !this.items.size || this.items.has(this.hashFunction(date));
	}
}

class DateListController extends toggles.TogglesController {
	constructor(options) {
		super(Object.assign({
			coloured: false, css: 'cm-toggles',
			getTitle: function (v) {
				return formatDate(v, DateFormat);
			}
		}, options));
	}

	reset() {
		this.toggles.items.clear();
		this.update();
	}
}

const TIME_RANGE_TEMPLATE = require('../templates/time-range-narrow.html');
class TimeRangeController extends TimeRange {
	constructor(options) {
		super({minRange: 14 * TS.DAY, template: TIME_RANGE_TEMPLATE}, options);
	}

	range() {
		return TS.domain(this.timeFrom, this.timeTo);
	}

	has(value) {
		return this.timeTo >= value && value >= this.timeFrom;
	}

	defaultTimeRange() {
		let t = TS.addDays(TS.day(new Date()), 1);
		return {timeFrom: TS.alter(t, -TS.DAY * 30), timeTo: t}
	}
}

class EnvironmentToggles extends toggles.Toggles {
	constructor(options) {
		super(null, options);
		_.bindAll(this, 'addValue', 'addSummary');
	}

	reset() {
		for (let toggle of this.items.values()) {
			toggle._value = 0;
		}
		return this;
	}

	addValue(value, key) {
		if (key === 'date') {
			return;
		}
		let items = this.items;
		if (items.has(key)) {
			items.get(key)._value += value;
		} else {
			this.add(key, value);
		}
	}

	addSummary(summary) {
		_.forEach(summary, this.addValue);
		return this;
	}
}

const CHART_BLOCK_TEMPLATE = require('../templates/chart-block.html');
const ERROR_TEMPLATE = require('../templates/chart-block-no-data.html');
const HOURS_DOMAIN = _.range(0, 24);

class ChartBlock extends APIController {
	constructor(... options) {
		super({
			method: 'consumption/by-filter',
			mergeCustomiser: ChartBlock.mergeCustomiser
		}, ... arguments);
		this._byDay = new Map();
		this._byHour = new Map();
		this._environments = new Set();
		this._dates = this.options.dates;

		// This one for event listeners
		this._redrawHandler = _.ary(this.redraw, 0).bind(this);
	}

	getApiCallOptions(options) {
		options = super.getApiCallOptions(options);
		options.params = Object.assign({groupBy: 'environment'}, options.params);
		return options;
	}

	summaryValue(summary) {
		let {valueGetter} = this.options;
		if (valueGetter) {
			return valueGetter(summary) || 0;
		} else {
			return summary.total || 0;
		}
	}

	stackOperator(stackValue, summary) {
		stackValue = stackValue || 0;
		let summaryValue = this.summaryValue(summary);
		let {stackOperator} = this.options;
		if (stackOperator) {
			return stackOperator(stackValue, summaryValue);
		} else {
			return stackValue + summaryValue;
		}
	}

	initStack(stacks, key) {
		let stackKey;
		if (key instanceof Date) {
			stackKey = key.toISOString();
		} else {
			stackKey = String(key);
		}
		let stack = stacks.get(stackKey);
		if (!stack) {
			stack = Object.create(null);
			stack.key = key;
			stacks.set(stackKey, stack);
		}
		return stack;
	}

	stackSummary(stacks, key, summary) {
		let environment = _.get(summary, 'filters.environment');
		if (environment) {
			let stack = this.initStack(stacks, key);
			stack[environment] = this.stackOperator(stack[environment], summary);
			this._environments.add(environment);
		}
	}

	shownLayers() {
		let layers = Array.from(this._environments);
		layers.sort();
		return layers;
	}

	allLayers() {
		return this.shownLayers();
	}

	filterData(data) {
		data = data || this.options.data;
		this._byDay.clear();
		this._byHour.clear();
		this._environments.clear();
		for (let summary of data) {
			let ts = TS.validate(summary.ts);
			let day = TS.day(ts);
			this.stackSummary(this._byDay, day, summary);
			if (this._dates.has(day)) {
				this.stackSummary(this._byHour, ts.getHours(), summary);
			}
		}
	}

	processResponse(response) {
		let data = response.data;
		this.options.data = data;
		this.filterData(data);
		return response;
	}

	drawDailyBars(options) {
		options = options || {};
		let {timeRange} = options;
		if (timeRange) {
			this._xDomain = timeRange.slice(0, -1);
		} else if (!this._xDomain) {
			throw new Error('Provide time range for daily chart');
		}

		let chartDOM = this.container.querySelector('.cm-chart-daily');
		chartDOM.innerHTML = '';

		let layers = this.shownLayers();
		let values = Array.from(this._byDay.values());
		let chart = new DBars({
			dates: this._dates, allLayers: layers, layers: layers,
			xDomain: this._xDomain
		});
		chart.draw({node: chartDOM, data: values});
		chart.on('cm:click', this._redrawHandler);
	}

	drawHourlyBars(options) {
		let chartDOM = this.container.querySelector('.cm-chart-hourly');
		chartDOM.innerHTML = '';
		let layers = this.shownLayers();
		let values = Array.from(this._byHour.values());
		let chart = new HBars({allLayers: layers, layers: layers, xDomain: HOURS_DOMAIN});
		chart.draw({node: chartDOM, data: values});
	}

	draw(options) {
		options = super.draw(options);
		this.drawDailyBars(options);
		this.drawHourlyBars(options);
		return options;
	}

	redraw(options) {
		this.filterData();
		this.drawDailyBars();
		this.drawHourlyBars();
		return options;
	}

	static mergeCustomiser(a, b, key) {
		if (key === 'data') {
			let concat = [];
			if (Array.isArray(a)) {
				concat.push(a);
			}
			if (Array.isArray(b)) {
				concat.push(b);
			}
			return [].concat(... concat);
		}
	}
}

class ChartPage extends Controller {
	constructor(... options) {
		super({
			title: 'Minutes',
			template: CHART_BLOCK_TEMPLATE, errorTemplate: ERROR_TEMPLATE}, ... arguments);
		this.filters = new FiltersController();
		this.timeRangeController = new TimeRangeController();
		this.dates = new DateListToggles();
		this.dateListController = new DateListController({toggles: this.dates});
		this.charts = this.initChartsBlock();
	}

	initChartsBlock() {
		throw new Error('Not implemented');
	}

	render(options) {
		let self = this;
		return super.render(options)
			.then(function (options) {
				let c = self.container;
				return Promise.all([
					self.filters.load({container: c.querySelector('.cm-filters')}),
					self.timeRangeController.render({container: c.querySelector('.cm-time-range')}),
					self.dateListController.render({container: c.querySelector('.cm-dates')})
				])
					.then(function () {
						self.renderCharts();
					})
					.then(function () {
						let update = _.ary(self.update.bind(this));
						// self.timeRangeController.addListener('cm:update', update);
						// self.filters.addListener('cm:update', update);

						// Buttons
						c.querySelector('.cm-refresh').addEventListener('click', function (e) {
							e.preventDefault();
							self.update();
						});
						c.querySelector('.cm-reset').addEventListener('click', function (e) {
							e.preventDefault();
							self.filters.reset();
							self.timeRangeController.reset();
							self.dateListController.reset();
							self.update();
						});

						// Dates list interaction without reloading, might be a bit inconsistent with others,
						// but it's faster and you still can hit refresh if you have to...
						self.dates.addListener('cm:update', function () {
							self.dateListController.update();
						});
						self.dateListController.addListener('cm:update', function () {
							self.charts.redraw();
						});
						return options;
					});
			});
	}

	getQueryParams() {
		return Object.assign(
			Object.create(null),
			this.timeRangeController.getQueryParams(),
			this.filters.getQueryParams()
		);
	}

	renderCharts() {
		let c = this.container;
		return this.charts.load({
			container: c, params: this.getQueryParams(),
			timeRange: this.timeRangeController.range(),
		});
	}

	update(options) {
		return this.renderCharts()
			.then(function () {
				return options;
			});
	}
}

class MinutesChart extends ChartPage {
	constructor() {
		super({title: 'Minutes'});
	}

	initChartsBlock() {
		return new ChartBlock({
			title: 'Minutes', dates: this.dates,
			valueGetter: function (s) {
				return Math.ceil(s.total / TS.MINUTE);
			}
		});
	}
}

class StreamsChart extends ChartPage {
	constructor() {
		super({title: 'Streams'});
	}

	initChartsBlock() {
		return new ChartBlock({
			title: 'Streams', dates: this.dates,
			valueGetter: function (s) {
				return s.count;
			}
		});
	}
}

class CCUChart extends ChartPage {
	constructor() {
		super({title: 'CCU'});
	}

	initChartsBlock() {
		return new ChartBlock({
			title: 'Streams', dates: this.dates,
			valueGetter: function (s) { return s.ccu; },
			stackOperator: Math.max
		});
	}
}

module.exports = {MinutesChart, StreamsChart, CCUChart};
